/**
 * Schema.org (JSON-LD) builders for SEO rich snippets.
 * Used via the `jsonLd` prop of <SeoHelmet />.
 */

const ORG_NAME_RU = 'ВЕЛОКС ХИНТ';
const ORG_NAME_EN = 'VELOX HINT';

const origin = () => (typeof window !== 'undefined' ? window.location.origin : '');

/** Strip markdown links `[text](url)` and HTML tags to plain text. */
export const toPlainText = (str = '') =>
  str
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const organizationSchema = ({ language = 'RU', settings = {} } = {}) => {
  const name = language === 'EN' ? ORG_NAME_EN : ORG_NAME_RU;
  const phone = settings.phone || '+7 (495) 123-45-67';
  const email = settings.email || 'mail@velox-hint.com';
  const sameAs = [settings.telegram].filter(Boolean);
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url: origin(),
    logo: `${origin()}/logo512.png`,
    description:
      language === 'EN'
        ? 'Customs clearance, certification and foreign-trade consulting in Russia and the EAEU.'
        : 'Таможенное оформление, сертификация и консалтинг в сфере ВЭД в России и ЕАЭС.',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: phone,
      email,
      contactType: 'customer service',
      areaServed: 'RU',
      availableLanguage: ['Russian', 'English'],
    },
    ...(sameAs.length ? { sameAs } : {}),
  };
};

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: ORG_NAME_RU,
  url: origin(),
});

export const serviceSchema = ({ service, language = 'RU' }) => {
  const orgName = language === 'EN' ? ORG_NAME_EN : ORG_NAME_RU;
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: toPlainText(service.seo_description || service.description || '').slice(0, 300),
    serviceType: service.title,
    provider: {
      '@type': 'Organization',
      name: orgName,
      url: origin(),
    },
    areaServed: { '@type': 'Country', name: 'Russia' },
    url: `${origin()}/services/${service.slug}`,
  };
};

export const breadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url || `${origin()}${it.path || ''}`,
  })),
});

export const faqPageSchema = (faqs = []) => {
  const mainEntity = faqs
    .filter((f) => f.question && f.answer)
    .slice(0, 50)
    .map((f) => ({
      '@type': 'Question',
      name: toPlainText(f.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: toPlainText(f.answer),
      },
    }));
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
};
